// Time-lapse (the "making of" replay): turns a project's saved versions into the frames the replay
// shows, each with a caption saying what the student asked next. Pure, so it is tested without a browser.

/** A saved version, as the API lists it (newest first in the API; order is fixed here). */
export interface LapseCheckpoint {
  id: number;
  reason: string;
  createdAt: number;
}

export interface LapseFrame {
  /** The saved version to show, or null for the project as it is now (always the last frame). */
  id: number | null;
  /** What the student did next, in their words. */
  caption: string;
  createdAt: number;
}

export interface TimeLapse {
  /** Oldest version first, then "now". One frame means there is nothing to replay yet. */
  frames: LapseFrame[];
  /** When the oldest kept version was saved. The server keeps only the last 50, so this may not be the first build. */
  startedAt: number | null;
}

/** The caption for a version, from the reason it was saved under (see apps/api/src/index.ts). */
export function lapseCaption(reason: string): string {
  if (reason === "Before: approved plan") return "Then the helper built the plan you approved.";
  if (reason.startsWith("Before: ")) {
    const asked = reason.slice(8).trim();
    // Build turns with no message save "Before: " with nothing after it.
    return asked ? `Then you asked: “${asked}”` : "Then the helper made a change.";
  }
  if (reason === "Before restoring an earlier version") return "Then you went back to an earlier version.";
  if (reason === "Save point") return "Then you made a save point.";
  return `Then: ${reason}`;
}

export function timeLapse(checkpoints: LapseCheckpoint[], now: number): TimeLapse {
  const saved = [...checkpoints].sort((a, b) => a.id - b.id);
  const frames: LapseFrame[] = saved.map((c) => ({ id: c.id, caption: lapseCaption(c.reason), createdAt: c.createdAt }));
  frames.push({ id: null, caption: "This is how your project is now.", createdAt: now });
  return { frames, startedAt: saved[0]?.createdAt ?? null };
}
